import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import classNames from 'classnames';
import { get, trim, isEmpty } from 'lodash';
import moment from 'moment';
import { withRouter } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';

import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TablePagination,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  FirstPage as FirstPageIcon,
  FilterList as FilterListIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  LastPage as LastPageIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
} from '@material-ui/icons';

import {
  fetchNotification,
  fetchNotificationPage,
  notificationMarkRead,
} from './actions';
import { texts, handleNavigation } from '../../config/constants/notification';
import { loadTexts } from '../../config/texts';

const TEXT_KEY = "notification";
const styles = theme => ({
  readCell: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  row: {
    backgroundColor: '#c4ceff',
  },
  actions: {
    flexShrink: 0,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing.unit * 2.5,
  },
  toolbar: {
    paddingLeft: theme.spacing.unit * 2,
    paddingRight: theme.spacing.unit * 1,
  },
  spacer: {
    flex: '1 1 100%',
  },
  filterRead: {
    backgroundColor: theme.palette.primary.light,
  },
});

class Notification extends React.Component {
  state = {
    currentPage: 1,
    onlyUnread: false,
    texts: loadTexts(TEXT_KEY, this.props.locale)
  };

  rowsPerPage = 13;

  componentDidMount() {
    this.fetchNotification();
  }

  _onClickMark = async (event, _id) => {
    event.stopPropagation();
    await this.props.notificationMarkRead({ notification: [_id] });
    this.fetchNotification();
    this.props.globalFetchNotification({ paginationInput: { page: 1, limit: 7 } });
  };

  _onChangePage = (event, page) => {
    this.fetchNotification(page + 1);
    this.setState({ currentPage: page + 1 });
  };
  _onClickNotif = async notif => {
    if (!notif.read) {
      await this.props.notificationMarkRead({ notification: [notif._id] });
      this.fetchNotification();
    }
    const { key, payload } = notif;
    const { history } = this.props;
    handleNavigation({ history, key, payload });
  };
  _onClickFirstPage = event => this._onChangePage(event, 0);
  _onClickPrevPage = event => this._onChangePage(event, this.state.currentPage - 2);
  _onClickNextPage = event => this._onChangePage(event, this.state.currentPage);
  _onClickLastPage = event => this._onChangePage(event, this.props.pageCount - 1);

  _onClickFilter = async () => {
    await this.fetchNotification(
      this.state.currentPage,
      this.rowsPerPage,
      !this.state.onlyUnread
    );
    this.setState(prevState => ({ onlyUnread: !prevState.onlyUnread }));
  };

  fetchNotification = (
    page = this.state.currentPage,
    limit = this.rowsPerPage,
    onlyUnread = this.state.onlyUnread
  ) => {
    const params = { paginationInput: { page, limit } };
    if (onlyUnread) params.read = false;
    return this.props.fetchNotification(params);
  }

  render() {
    const { notification, classes, count } = this.props;
    const { currentPage, onlyUnread, texts } = this.state;
    return (
      <Paper>
        <Toolbar className={classes.toolbar}>
          <div className={classes.spacer} />
          <Tooltip title={!onlyUnread ? texts.alert.only_unread : texts.alert.show_all}>
            <IconButton
              onClick={this._onClickFilter}
              className={classNames({ [classes.filterRead]: onlyUnread })}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
        <Table>
          <TableBody>
            {notification.map(notif => {
              const { createdAt, key, payload, read, _id } = notif;
              const date = moment(parseInt(createdAt.timestamp, 10));
              const text = texts[key] || key;
              return (
                <TableRow
                  key={_id}
                  classes={{ root: classNames({ [classes.row]: !read }) }}
                  onClick={() => this._onClickNotif(notif)}
                  hover
                >
                  <TableCell>
                    <div className={classes.readCell}>
                      <div>
                        <Typography>
                          {text.replace(/\[.*?\]/g, x => {
                            const variable = trim(x, '[]');
                            if (isEmpty(payload) || isEmpty(trim(payload[variable])))
                              return variable;
                            return payload[variable] || variable;
                          })}
                        </Typography>
                        <Typography variant="caption">
                          {`${date.format('MMM D, Y')} at ${date.format('HH:mm')}`}
                        </Typography>
                      </div>
                      {!read && (
                        <Tooltip title={texts.alert.mark_read}>
                          <IconButton onClick={event => this._onClickMark(event, _id)}>
                            <RadioButtonCheckedIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={count}
                page={currentPage - 1}
                onChangePage={this._onChangePage}
                rowsPerPage={this.rowsPerPage}
                rowsPerPageOptions={[this.rowsPerPage]}
                ActionsComponent={() => (
                  <div className={classes.actions}>
                    <IconButton
                      onClick={this._onClickFirstPage}
                      disabled={currentPage === 1}
                      aria-label="First Page"
                    >
                      <FirstPageIcon />
                    </IconButton>
                    <IconButton
                      onClick={this._onClickPrevPage}
                      disabled={currentPage === 1}
                      aria-label="Previous Page"
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <IconButton
                      onClick={this._onClickNextPage}
                      disabled={currentPage >= Math.ceil(count / this.rowsPerPage)}
                      aria-label="Next Page"
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                    <IconButton
                      onClick={this._onClickLastPage}
                      disabled={currentPage >= Math.ceil(count / this.rowsPerPage)}
                      aria-label="Last Page"
                    >
                      <LastPageIcon />
                    </IconButton>
                  </div>
                )}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    );
  }
}

const mapStateToProps = state => ({
  notification: get(state, 'notification.page.data.notification') || [],
  count: get(state, 'notification.page.data.count') || 0,
  pageCount: get(state, 'notification.page.data.pageCount') || 0,
  locale: state.appState.locale,
});

const mapDispatchToProps = dispatch => ({
  fetchNotification: params => dispatch(fetchNotificationPage(params)()),
  globalFetchNotification: params => dispatch(fetchNotification(params)()),
  notificationMarkRead: params => dispatch(notificationMarkRead(params)()),
});

export default compose(
  withRouter,
  withStyles(styles, { name: 'Notification' }),
  connect(mapStateToProps, mapDispatchToProps)
)(Notification);



// WEBPACK FOOTER //
// ./src/modules/notification/Scene.js